// ============================================================================
// UNIVERSAL ACTION HANDLERS - STATUS MANAGEMENT ACTIONS
// ============================================================================

import type { UniversalActionHandlers } from "./types";
import { getSupabase } from "@/lib/hooks/api/utils";
import { CLOSED_TICKET_STATUSES } from "@/lib/constants/ticket-statuses";
import { selfAssign } from "./utils/self-assign";

/**
 * Status Management Actions
 */
export const statusManagementActions: UniversalActionHandlers = {
  // Quick Complete action — shared across work-item entities (tasks + tickets).
  // "Complete" means different terminal statuses per entity: tasks use the
  // task-status "completed", but a ticket has NO "completed" value in the
  // `ticket_status` enum (new/assigned/in_progress/waiting_for_customer/
  // waiting_for_vendor/resolved/closed/cancelled). For a ticket, completing =
  // resolving: set status "resolved" + stamp resolved_at, mirroring the
  // canonical markTicketResolvedFromSlack path. Branching on the wrong status
  // for tickets throws a Postgres 22P02 (invalid enum) — the bug this fixes.
  completeTask: async (item, context) => {
    if (context.entityName === "tickets") {
      const currentStatus = (item as { status?: string | null }).status ?? null;
      // Idempotent: a ticket already resolved/closed/cancelled needs no update.
      if (
        currentStatus &&
        (CLOSED_TICKET_STATUSES as readonly string[]).includes(currentStatus)
      ) {
        context.onToast("Ticket is already resolved", "info");
        return;
      }
      // updated_at is DB-managed (excluded from the API); only status +
      // resolved_at are written here.
      await context.onUpdateItem({
        status: "resolved",
        resolved_at: new Date().toISOString(),
      });
      context.onToast("Ticket resolved successfully", "success");
      return;
    }

    await context.onUpdateItem({ status: "completed" });
    context.onToast("Task completed successfully", "success");
  },

  // Close ticket action - opens close ticket modal
  closeTicket: async (item, context) => {
    context.onOpenModal("close-ticket", {
      ticketId: item.ticket_id || item.id,
      item,
    });
  },

  // Assign ticket action - opens assign ticket modal
  assignTicket: async (item, context) => {
    context.onOpenModal("assign-ticket", {
      ticketId: item.ticket_id || item.id,
      item,
    });
  },

  // Assign ticket to me - sets assigned_to to the logged-in user (single-user field)
  assignTicketToMe: selfAssign({
    field: "assigned_to",
    mode: "scalar",
    messages: {
      alreadySet: "This ticket is already assigned to you.",
      success: "Ticket assigned to you.",
    },
  }),

  // Assign task to me - adds the logged-in user to assigned_users (multi-user field)
  assignTaskToMe: selfAssign({
    field: "assigned_users",
    mode: "array",
    messages: {
      alreadySet: "This task is already assigned to you.",
      success: "Task assigned to you.",
    },
  }),

  // Assign deal to me - adds the logged-in user to the deal's owners (assigned_users, multi-user)
  assignDealToMe: selfAssign({
    field: "assigned_users",
    mode: "array",
    messages: {
      alreadySet: "You're already an owner of this deal.",
      success: "You're now an owner of this deal.",
    },
  }),

  // Approve/Disapprove time entry action - toggles approval status (admin only)
  approveTimeEntry: async (item, context) => {
    try {
      // Toggle approval status
      const newApprovalStatus = !item.is_approved;
      const action = newApprovalStatus ? "approved" : "disapproved";

      // Prepare update object
      const updateData: { is_approved: boolean; sync_status?: string } = {
        is_approved: newApprovalStatus,
      };

      // If approving and sync_status was "disapproved", reset it to "pending"
      if (newApprovalStatus && (item).sync_status === "disapproved") {
        updateData.sync_status = "pending";
      }

      // Update the time entry with toggled approval status (and sync_status if needed)
      await context.onUpdateItem(updateData);
      context.onToast(`Time entry ${action} successfully`, "success");
    } catch (error) {
      console.error("Error toggling time entry approval:", error);
      const action = item.is_approved ? "disapprove" : "approve";
      context.onToast(`Failed to ${action} time entry`, "error");
    }
  },

  // Reject time entry action - marks time entry as disapproved (admin only).
  // There is NO `is_rejected` column on `time_entries`. Denial = is_approved:false
  // + sync_status:"disapproved" (keeps the entry out of the QB sync queue).
  // Mirrors the server-side denyTimeEntryFromSlack reference implementation.
  rejectTimeEntry: async (item, context) => {
    try {
      // Already disapproved? No-op.
      if ((item as { sync_status?: string | null }).sync_status === "disapproved") {
        context.onToast("Time entry is already rejected", "info");
        return;
      }

      await context.onUpdateItem({ is_approved: false, sync_status: "disapproved" });
      context.onToast("Time entry rejected successfully", "success");
    } catch (error) {
      console.error("Error rejecting time entry:", error);
      context.onToast("Failed to reject time entry", "error");
    }
  },

  // Move to Internal Projects action - moves time entry and ticket to Internal Projects contact (admin only)
  moveToInternalProjects: async (item, context) => {
    try {
      console.log("🔄 moveToInternalProjects: Starting action for item:", item);
      const supabase = getSupabase();

      // 1. Find "Internal Projects" contact
      const { data: contacts, error: contactError } = await supabase
        .from("contacts")
        .select("id, company_id")
        .eq("name", "Internal Projects");

      if (contactError) {
        console.error("❌ moveToInternalProjects: Error finding contact:", contactError);
        throw new Error(`Failed to find Internal Projects contact: ${contactError.message}`);
      }

      if (!contacts || contacts.length === 0) {
        console.error("❌ moveToInternalProjects: No Internal Projects contact found");
        context.onToast("Internal Projects contact not found", "error");
        return;
      }

      if (contacts.length > 1) {
        console.error(
          "❌ moveToInternalProjects: Multiple Internal Projects contacts found:",
          contacts.length
        );
        context.onToast(
          "Multiple Internal Projects contacts found. Please ensure only one exists.",
          "error"
        );
        return;
      }

      const internalProjectsContact = contacts[0];
      console.log(
        "✅ moveToInternalProjects: Found Internal Projects contact:",
        internalProjectsContact
      );

      // 2. Get the ticket associated with the time entry
      const ticketId = item.ticket_id || item.id; // Support both time entry and ticket contexts
      if (!ticketId) {
        console.error("❌ moveToInternalProjects: No ticket_id found in item:", item);
        context.onToast("No ticket associated with this item", "error");
        return;
      }

      console.log("🔄 moveToInternalProjects: Updating ticket:", ticketId);

      // 3. Update the ticket's contact_id AND company_id. No DB trigger derives
      // tickets.company_id from contact_id — sync_ticket_reporting_fields sets
      // company_name FROM company_id, not from the contact. So we must set both
      // explicitly, or company_id (and time_entries.company_name via
      // cascade_ticket_to_time_entries) stays pointed at the OLD company.
      const { data: updatedTicket, error: ticketError } = await supabase
        .from("tickets")
        .update({
          contact_id: internalProjectsContact.id,
          company_id: (internalProjectsContact.company_id as string | null) ?? null,
        })
        .eq("id", ticketId)
        .select("id, contact_id, company_id")
        .single();

      if (ticketError) {
        console.error("❌ moveToInternalProjects: Error updating ticket:", ticketError);
        throw new Error(`Failed to update ticket: ${ticketError.message}`);
      }

      console.log("✅ moveToInternalProjects: Ticket updated successfully:", updatedTicket);

      // 4. Success - time entry will automatically update via database triggers
      context.onToast("Time entry and ticket moved to Internal Projects", "success");
      await context.onRefresh();
    } catch (error) {
      console.error("❌ moveToInternalProjects: Unexpected error:", error);
      context.onToast(
        error instanceof Error ? error.message : "Failed to move to Internal Projects",
        "error"
      );
    }
  },

  // Reassign company/contact (admin only). Opens a contact picker; on confirm,
  // updates the PARENT ticket's contact_id AND company_id explicitly (no DB trigger
  // derives ticket.company_id from contact_id). DB triggers then cascade the
  // denormalized company_name/contact_name onto the ticket and ALL its time entries.
  // The time_entry.ticket_id link is never touched — the relationship stays intact.
  // Works from a ticket row (item.id) or a time-entry row (item.ticket_id).
  reassignTicketCompany: async (item, context) => {
    const ticketId = (item as { ticket_id?: string | null }).ticket_id || item.id;
    if (!ticketId) {
      context.onToast("No ticket associated with this item", "error");
      return;
    }
    context.onOpenModal("reassign-company-contact", {
      item,
      ticketId,
      onConfirm: async (contactId: string) => {
        const supabase = getSupabase();
        // Resolve the chosen contact's company so we can set ticket.company_id
        // explicitly — the reporting trigger derives company_name from company_id,
        // not from contact_id, so we must set both.
        const { data: contact, error: contactError } = await supabase
          .from("contacts")
          .select("company_id")
          .eq("id", contactId)
          .single();
        if (contactError) {
          throw new Error(`Failed to load contact: ${contactError.message}`);
        }
        const { error: ticketError } = await supabase
          .from("tickets")
          .update({
            contact_id: contactId,
            company_id: (contact?.company_id as string | null) ?? null,
          })
          .eq("id", ticketId);
        if (ticketError) {
          throw new Error(`Failed to update ticket: ${ticketError.message}`);
        }
        await context.onRefresh();
      },
    });
  },

  // Push time entries for (re-)approval (admin only). Resets non-synced entries to
  // pending and re-posts a fresh Slack approval card so they can be approved/denied
  // against the corrected company/contact. Entries already synced to QuickBooks are
  // SKIPPED (never silently re-queued — avoids double billing, per
  // TIME_ENTRY_BILLING_VIA_APPROVAL_AND_QB_SYNC + QUICKBOOKS_INVOICE_IDEMPOTENCY).
  // From a time-entry row → that one entry; from a ticket row → all its entries.
  pushTimeEntryForApproval: async (item, context) => {
    try {
      const row = item as Record<string, unknown>;
      const ticketIdOnRow = row.ticket_id as string | null | undefined;
      // A time-entry row carries the ticket_id FK; a ticket row does not.
      const payload = ticketIdOnRow
        ? { timeEntryId: row.id as string }
        : { ticketId: row.id as string };

      const supabase = getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        context.onToast("Not authenticated", "error");
        return;
      }

      const res = await fetch("/api/time-entries/push-approval", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const result = (await res.json()) as {
        pushed?: number;
        skippedSynced?: number;
        error?: string;
      };
      if (!res.ok) {
        context.onToast(result.error || "Failed to push for approval", "error");
        return;
      }
      const pushed = result.pushed ?? 0;
      const skipped = result.skippedSynced ?? 0;
      const parts = [`Pushed ${pushed} time ${pushed === 1 ? "entry" : "entries"} for approval`];
      if (skipped > 0) {
        parts.push(`${skipped} already synced to QuickBooks — correct in QB`);
      }
      context.onToast(parts.join(" · "), pushed > 0 ? "success" : "info");
    } catch (error) {
      console.error("Error pushing time entry for approval:", error);
      context.onToast("Failed to push for approval", "error");
    }
  },
};
